import { downloadBlobAsFile } from "@jugl-web/domain-resources/drive";
import { useRestApiProvider } from "@jugl-web/rest-api";
import { PlainButton } from "@jugl-web/ui-kit";
import { cx, getAudioWaveform, normalizeAudioAmps } from "@jugl-web/utils";
import { VoiceMessageAudioPlayer } from "@web-src/modules/chats/components/CommonMessageBubble/components/AudioAttachment/components/VoiceMessageAudioPlayer";
import { useEntitySelectedProvider } from "@web-src/modules/entities/providers/EntityProvider/useEntitySelectedProvider";
import getBlobDuration from "get-blob-duration";
import React, { useEffect, useState } from "react";
import { ReactComponent as DownloadIcon } from "./assets/download.svg";

export const VoiceConversationMediaPlayer: React.FC<{
  mediaId: string;
  className?: string;
}> = ({ mediaId, className }) => {
  const { entityId } = useEntitySelectedProvider();
  const { integrationsApi } = useRestApiProvider();

  const [waveform, setWaveform] = useState<number[]>([0]);
  const [audioSrc, setAudioSrc] = useState("");
  const [duration, setDuration] = useState<number>();

  const { data: audioBlob, isFetching } = integrationsApi.useGetVoiceMediaQuery(
    { entityId, mediaId }
  );

  useEffect(() => {
    if (!audioBlob) {
      setWaveform([0]);
      setAudioSrc("");
      setDuration(undefined);
      return undefined;
    }

    const objectUrl = URL.createObjectURL(audioBlob);
    setAudioSrc(objectUrl);

    let cancelled = false;

    const loadAudioData = async () => {
      try {
        const file = new File([audioBlob], `${mediaId}.wav`, {
          type: audioBlob.type || "audio/wav",
        });
        const [amps, audioDuration] = await Promise.all([
          getAudioWaveform(file),
          getBlobDuration(objectUrl),
        ]);

        if (!cancelled) {
          setWaveform(amps);
          setDuration(audioDuration);
        }
      } catch {
        if (!cancelled) {
          setWaveform([0]);
          setDuration(undefined);
        }
      }
    };

    loadAudioData();

    return () => {
      cancelled = true;
      URL.revokeObjectURL(objectUrl);
    };
  }, [audioBlob, mediaId]);

  const containerClassName = cx(
    "flex items-center gap-4 rounded-lg border border-solid border-grey-300 bg-white p-3.5",
    className
  );

  const isPreparingAudio = Boolean(audioBlob) && !audioSrc;
  const showSkeleton = !audioSrc && (isFetching || isPreparingAudio);

  if (showSkeleton) {
    return (
      <div className={containerClassName} aria-busy="true">
        <div className="flex min-w-0 flex-1 animate-pulse items-center gap-3">
          <div className="h-9 w-9 shrink-0 rounded-full bg-grey-200" />
          <div className="h-8 min-w-0 flex-1 rounded bg-grey-200" />
          <div className="h-3.5 w-10 shrink-0 rounded bg-grey-200" />
        </div>
        <div className="h-5 w-5 shrink-0 rounded bg-grey-200" />
      </div>
    );
  }

  if (!audioSrc) {
    return null;
  }

  return (
    <div className={containerClassName}>
      <div className="min-w-0 flex-1">
        <VoiceMessageAudioPlayer
          audioSrc={audioSrc}
          waveform={normalizeAudioAmps(waveform)}
          duration={duration}
          variant="compact"
        />
      </div>
      <PlainButton
        onClick={() => {
          if (audioBlob) {
            downloadBlobAsFile(audioBlob, `${mediaId}.wav`);
          }
        }}
        disabled={!audioBlob}
      >
        <DownloadIcon />
      </PlainButton>
    </div>
  );
};
